<template>
	<!-- Destino de ?focus=establishments (aviso 604 al facturar sin dirección). -->
	<div class="card" :class="{'focus-highlight': highlighted}">
		<div class="section-header section-header-action">
			<div>
				<h5 class="section-title">Establecimientos y puntos de emisión</h5>
				<p class="section-desc">Cada local registrado en tu RUC es un establecimiento. Cada caja o equipo que factura en él es un punto de emisión, con su propia numeración.</p>
			</div>
			<Button label="Agregar establecimiento" icon="pi pi-plus" class="p-button-sm header-button" @click="openNewEstablishment" />
		</div>

		<Message v-if="missing.length" severity="error" :closable="false" class="mt-0">
			<template v-if="missing.length === 1">El establecimiento <b>{{ missing[0].code }}</b> no tiene dirección.</template>
			<template v-else>Los establecimientos <b>{{ missing.map(e => e.code).join(', ') }}</b> no tienen dirección.</template>
			El SRI rechaza sus comprobantes hasta que la completes.
		</Message>

		<p v-if="loaded && !establishments.length" class="empty">Aún no hay establecimientos registrados.</p>

		<div class="estab-list">
			<article v-for="e in establishments" :key="e.id" class="estab" :class="{'estab-missing': !hasAddress(e)}" :aria-label="'Establecimiento ' + e.code">
				<div class="estab-head">
					<span class="estab-code">{{ e.code }}</span>
					<div class="estab-body">
						<div class="estab-name">
							{{ e.label }}
							<!-- Sin nombre propio ya se llama «Matriz»: la etiqueta solo repetiría. -->
							<Tag v-if="e.isMain && e.label !== 'Matriz'" value="MATRIZ" class="tag-main" />
						</div>
						<div v-if="hasAddress(e)" class="estab-address">{{ e.address }}</div>
						<div v-else class="estab-no-address">
							<Tag value="SIN DIRECCIÓN" severity="danger" />
							<Button label="Completar dirección" class="p-button-outlined p-button-sm" @click="openEditEstablishment(e)" />
						</div>
					</div>
					<div class="estab-actions">
						<Button icon="pi pi-pencil" class="p-button-rounded p-button-text p-button-plain" v-tooltip.top="'Editar'"
							:aria-label="'Editar el establecimiento ' + e.code" @click="openEditEstablishment(e)" />
						<span v-if="!e.isMain" v-tooltip.top="e.canDelete ? 'Eliminar' : 'No se puede eliminar: ya emitió comprobantes o lo usa una actividad'">
							<Button icon="pi pi-trash" class="p-button-rounded p-button-text p-button-danger" :disabled="!e.canDelete"
								:aria-label="'Eliminar el establecimiento ' + e.code" @click="confirmDeleteEstablishment(e)" />
						</span>
					</div>
				</div>
				<ul class="points">
					<li v-for="p in e.points" :key="p.id" class="point">
						<span class="series">{{ p.series }}</span>
						<span class="point-users">
							<template v-if="p.activities.length">Usado por <b>{{ p.activities.map(a => a.name).join(', ') }}</b></template>
							<template v-else>Ninguna actividad lo usa todavía</template>
						</span>
						<span class="point-last">
							<template v-if="lastReceipt(p)">Última factura <span class="series series-soft">{{ lastReceipt(p) }}</span></template>
							<template v-else>Sin facturas</template>
							<span v-if="e.points.length > 1" v-tooltip.top="canDeletePoint(p) ? 'Eliminar' : 'No se puede eliminar: ya emitió comprobantes o lo usa una actividad'">
								<Button icon="pi pi-trash" class="p-button-rounded p-button-text p-button-danger p-button-sm" :disabled="!canDeletePoint(p)"
									:aria-label="'Eliminar el punto ' + p.series" @click="confirmDeletePoint(p)" />
							</span>
						</span>
					</li>
				</ul>
				<div class="add-point">
					<Button :label="'Agregar punto de emisión en ' + e.code" icon="pi pi-plus" class="p-button-text p-button-sm" @click="openNewPoint(e)" />
				</div>
			</article>
		</div>

		<ConfirmDialog group="establishments" />

		<!-- Alta o edición de un establecimiento -->
		<Dialog v-model:visible="establishmentDialog" :header="editing ? 'Establecimiento ' + editing.code : 'Nuevo establecimiento'"
			:modal="true" class="p-fluid" :style="{width: '36rem'}" :breakpoints="{'640px': '94vw'}" @after-hide="resetDialogs">
			<p class="dialog-desc">
				<template v-if="!editing">Un local registrado en tu RUC, con su primer punto de emisión.</template>
				<template v-else-if="editing.isMain">Tu matriz. Su dirección sale como «Dirección matriz» en todos tus comprobantes.</template>
			</p>
			<div v-if="editing" class="field">
				<span class="field-label">Código</span>
				<span class="series series-lg">{{ editing.code }}</span>
				<small class="field-help">El código no se cambia: ya numera tus comprobantes.</small>
			</div>
			<Message v-if="formError" severity="error" :closable="false">{{ formError }}</Message>
			<EmissionFields v-model="draft" :errors="errors" :mode="editing ? 'edit' : 'establishment'"
				:isMain="!!(editing && editing.isMain)" idPrefix="estab" />
			<template #footer>
				<Button label="Cancelar" icon="pi pi-times" class="p-button-text" @click="establishmentDialog = false" />
				<Button :label="editing ? 'Guardar' : 'Guardar establecimiento'" icon="pi pi-check" class="p-button-success"
					:loading="saving" @click="saveEstablishment" />
			</template>
		</Dialog>

		<!-- Punto nuevo en un establecimiento -->
		<Dialog v-model:visible="pointDialog" header="Nuevo punto de emisión" :modal="true" class="p-fluid"
			:style="{width: '34rem'}" :breakpoints="{'640px': '94vw'}" @after-hide="resetDialogs">
			<p v-if="pointEstablishment" class="dialog-desc">
				En {{ pointEstablishment.code }} · {{ pointEstablishment.label }}. Agrega uno por cada caja o equipo que factura en este local.
			</p>
			<Message v-if="formError" severity="error" :closable="false">{{ formError }}</Message>
			<EmissionFields v-if="pointEstablishment" v-model="draft" :errors="errors" mode="point"
				:establishmentCode="pointEstablishment.code" idPrefix="point" />
			<template #footer>
				<Button label="Cancelar" icon="pi pi-times" class="p-button-text" @click="pointDialog = false" />
				<Button label="Guardar punto" icon="pi pi-check" class="p-button-success" :loading="saving" @click="savePoint" />
			</template>
		</Dialog>
	</div>
</template>

<script>
	import httpRequest from '../../service/httpRequest';
	import EmissionFields from './EmissionFields.vue';
	import {
		emptySequentials, hasAddress, lastReceiptNumber, missingAddressList, sequentialsPayload, serverField,
		validateAddress, validateNewEstablishment, validateNewPoint, NAME_MAX_LENGTH,
	} from '../../service/establishments';

	const NETWORK_ERROR = 'No se pudo guardar. Revisa tu conexión e intenta de nuevo.';

	function blankDraft(code) {
		return { code: code || '', pointCode: '001', name: '', address: '', seq: emptySequentials() };
	}

	export default {
		name: 'EstablishmentsCard',
		components: { EmissionFields },
		props: {
			establishments: { type: Array, default: () => [] },
			nextEstablishmentCode: { type: String, default: null },
			loaded: { type: Boolean, default: false },
		},
		emits: ['updated'],
		data() {
			return {
				establishmentDialog: false,
				pointDialog: false,
				editing: null,            // establecimiento en edición (null = alta)
				pointEstablishment: null, // establecimiento al que va el punto nuevo
				draft: blankDraft(),
				errors: {},
				formError: null,
				saving: false,
				highlighted: false,
			}
		},
		computed: {
			missing() {
				return missingAddressList(this.establishments);
			},
		},
		methods: {
			hasAddress,
			lastReceipt(point) {
				return lastReceiptNumber(point);
			},
			canDeletePoint(point) {
				return !point.activities.length && !point.emitted;
			},

			openNewEstablishment() {
				this.resetDialogs();
				this.draft = blankDraft(this.nextEstablishmentCode);
				this.establishmentDialog = true;
			},
			// También lo usan Negocio («Editar» de la dirección matriz) y el aviso 604.
			openEditEstablishment(establishment) {
				this.resetDialogs();
				this.editing = establishment;
				const name = establishment.name && !/^\d+$/.test(establishment.name) ? establishment.name : '';
				this.draft = Object.assign(blankDraft(), { name, address: establishment.address || '' });
				this.establishmentDialog = true;
				this.focusAddress();
			},
			openEstablishmentById(id) {
				const establishment = this.establishments.find(e => e.id === id);
				if (establishment) { this.openEditEstablishment(establishment) }
			},
			// ?focus=establishments: resalta la tarjeta y, si hay un solo establecimiento sin
			// dirección, abre ese mismo para completarla.
			focusMissing() {
				this.highlighted = true;
				setTimeout(() => { this.highlighted = false }, 2600);
				if (this.missing.length === 1) { this.openEditEstablishment(this.missing[0]) }
			},
			openNewPoint(establishment) {
				this.resetDialogs();
				this.pointEstablishment = establishment;
				this.draft = Object.assign(blankDraft(), { pointCode: establishment.nextPointCode || '' });
				this.pointDialog = true;
			},
			resetDialogs() {
				this.editing = null;
				this.pointEstablishment = null;
				this.errors = {};
				this.formError = null;
				this.saving = false;
			},
			focusAddress() {
				this.$nextTick(() => setTimeout(() => {
					const input = document.getElementById('estab-address');
					if (input) { input.focus() }
				}, 150));
			},

			async saveEstablishment() {
				this.formError = null;
				if (this.editing) {
					const addressError = validateAddress(this.draft.address);
					this.errors = addressError ? { establishmentAddress: addressError } : {};
					if (String(this.draft.name || '').trim().length > NAME_MAX_LENGTH) {
						this.errors.establishmentName = `El nombre admite hasta ${NAME_MAX_LENGTH} caracteres.`;
					}
				} else {
					this.errors = validateNewEstablishment(this.draft, this.establishments);
				}
				if (Object.keys(this.errors).length) { return }

				const wasMissing = this.editing && !hasAddress(this.editing);
				const fields = this.editing
					? { id: this.editing.id, establishmentName: this.draft.name, establishmentAddress: this.draft.address }
					: {
						establishmentCode: this.draft.code, establishmentName: this.draft.name,
						establishmentAddress: this.draft.address, emitionPointCode: this.draft.pointCode,
						startSequentials: sequentialsPayload(this.draft.seq),
					};
				this.saving = true;
				const response = await httpRequest.saveEstablishment(fields);
				this.saving = false;
				if (this.handleFailure(response)) { return }

				this.$emit('updated', response.data);
				this.establishmentDialog = false;
				this.$toast.add({severity: 'success', summary: 'Guardado', life: 5000, detail: wasMissing
					? 'Listo. Las facturas que quedaron pendientes se enviarán solas al SRI en el próximo envío automático.'
					: (fields.id ? 'Los datos del establecimiento se actualizaron.' : `Establecimiento ${fields.establishmentCode.padStart(3, '0')} creado.`)});
			},
			async savePoint() {
				this.formError = null;
				this.errors = validateNewPoint(this.draft, this.pointEstablishment);
				if (Object.keys(this.errors).length) { return }

				this.saving = true;
				const response = await httpRequest.saveEmitionPoint({
					establishment: this.pointEstablishment.id, emitionPointCode: this.draft.pointCode,
					startSequentials: sequentialsPayload(this.draft.seq),
				});
				this.saving = false;
				if (this.handleFailure(response)) { return }

				const series = this.pointEstablishment.code + '-' + this.draft.pointCode.trim().padStart(3, '0');
				this.$emit('updated', response.data);
				this.pointDialog = false;
				this.$toast.add({severity: 'success', summary: 'Guardado', life: 5000,
					detail: `Punto ${series} creado. Elígelo en una actividad para facturar desde él.`});
			},
			// true si la respuesta no fue 200: marca el campo que dijo el backend o muestra el motivo.
			handleFailure(response) {
				if (response && response.status === 200) { return false }
				const field = serverField(response);
				if (field && field !== 'startSequentials') {
					this.errors = Object.assign({}, this.errors, {[field]: response.error});
				} else {
					this.formError = (response && response.error) || NETWORK_ERROR;
				}
				return true;
			},

			confirmDeleteEstablishment(establishment) {
				this.$confirm.require({
					group: 'establishments',
					header: 'Eliminar establecimiento',
					message: `¿Eliminar el establecimiento ${establishment.code}? Nunca emitió comprobantes y ninguna actividad lo usa.`,
					icon: 'pi pi-exclamation-triangle',
					acceptLabel: 'Sí, eliminar',
					rejectLabel: 'No',
					acceptClass: 'p-button-danger',
					accept: () => this.remove(httpRequest.deleteEstablishment(establishment.id), `Establecimiento ${establishment.code} eliminado.`),
				});
			},
			confirmDeletePoint(point) {
				this.$confirm.require({
					group: 'establishments',
					header: 'Eliminar punto de emisión',
					message: `¿Eliminar el punto ${point.series}? Nunca emitió comprobantes y ninguna actividad lo usa.`,
					icon: 'pi pi-exclamation-triangle',
					acceptLabel: 'Sí, eliminar',
					rejectLabel: 'No',
					acceptClass: 'p-button-danger',
					accept: () => this.remove(httpRequest.deleteEmitionPoint(point.id), `Punto ${point.series} eliminado.`),
				});
			},
			async remove(request, done) {
				const response = await request;
				if (response && response.status === 200) {
					this.$emit('updated', response.data);
					this.$toast.add({severity: 'success', summary: 'Eliminado', detail: done, life: 4000});
				} else {
					this.$toast.add({severity: 'error', summary: 'No se pudo eliminar', life: 8000,
						detail: (response && response.error) || NETWORK_ERROR});
				}
			},
		},
	}
</script>

<style scoped>
	.section-header { margin-bottom: 1.25rem; }
	.section-header-action {
		display: flex;
		align-items: flex-start;
		justify-content: space-between;
		gap: 1rem;
		flex-wrap: wrap;
	}
	.section-header-action > div { flex: 1 1 16rem; min-width: 0; }
	.section-title { font-size: 1.25rem; font-weight: 600; margin: 0 0 0.25rem 0; color: inherit; }
	.section-desc { margin: 0; line-height: 1.5; color: var(--text-color-secondary); }
	.header-button { width: auto; flex-shrink: 0; }
	.empty { color: var(--text-color-secondary); }
	.dialog-desc { margin: 0 0 1rem; color: var(--text-color-secondary); line-height: 1.5; }
	.dialog-desc:empty { display: none; }
	.field-label { display: block; font-weight: 500; margin-bottom: 0.5rem; color: var(--text-color); }
	.field-help { display: block; margin-top: 0.375rem; font-size: 0.9rem; color: var(--text-color-secondary); }

	.series {
		font-family: ui-monospace, Menlo, Consolas, monospace;
		font-variant-numeric: tabular-nums;
		font-weight: 600;
		letter-spacing: 0.02em;
		color: var(--text-color);
	}
	.series-soft { font-weight: 500; }
	.series-lg { font-size: 1.1rem; }

	.estab-list { display: grid; gap: 1rem; }
	.estab {
		border: 1px solid var(--surface-border);
		border-radius: 10px;
		background: var(--surface-ground);
		overflow: hidden;
	}
	/* Mismo rojo que p-invalid del tema. */
	.estab-missing { border-color: #e24c4c; }
	.estab-head {
		display: grid;
		grid-template-columns: auto 1fr auto;
		gap: 0.9rem;
		align-items: start;
		padding: 1rem 0.75rem 0.85rem 1rem;
	}
	.estab-code {
		font-family: ui-monospace, Menlo, Consolas, monospace;
		font-size: 1.1rem;
		font-weight: 700;
		letter-spacing: 0.06em;
		color: var(--text-color);
		background: var(--surface-card);
		border: 1px solid var(--surface-border);
		border-radius: 8px;
		padding: 0.35rem 0.55rem;
	}
	.estab-body { min-width: 0; }
	.estab-name {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 0.5rem;
		font-weight: 600;
		font-size: 1rem;
		color: var(--text-color);
	}
	.tag-main { background: #E0E7FF; color: #3730A3; }
	.estab-address { margin-top: 0.2rem; color: var(--text-color); overflow-wrap: anywhere; }
	.estab-no-address {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 0.5rem 0.75rem;
		margin-top: 0.4rem;
	}
	.estab-no-address .p-button { width: auto; }
	.estab-actions { display: flex; align-items: center; }

	.points {
		list-style: none;
		margin: 0;
		padding: 0;
		border-top: 1px solid var(--surface-border);
		background: var(--surface-card);
	}
	.point {
		display: grid;
		grid-template-columns: auto 1fr auto;
		gap: 0.25rem 1rem;
		align-items: center;
		padding: 0.6rem 0.75rem 0.6rem 1rem;
		border-bottom: 1px solid var(--surface-border);
		min-height: 3rem;
	}
	.point-users { min-width: 0; font-size: 0.9rem; color: var(--text-color-secondary); }
	.point-users b { font-weight: 500; color: var(--text-color); }
	.point-last {
		display: flex;
		align-items: center;
		gap: 0.35rem;
		justify-content: flex-end;
		font-size: 0.875rem;
		color: var(--text-color-secondary);
	}
	.add-point { padding: 0.25rem 0.5rem; background: var(--surface-card); }
	.add-point .p-button { width: auto; }

	@media (max-width: 575px) {
		.point { grid-template-columns: 1fr; }
		.point-last { justify-content: flex-start; }
	}

	.focus-highlight { animation: focus-highlight 2.5s ease-out; }
	@keyframes focus-highlight {
		0%, 50% { outline: 2px solid var(--primary-color); outline-offset: 4px; }
		100% { outline: 2px solid transparent; outline-offset: 4px; }
	}
	@media (prefers-reduced-motion: reduce) {
		.focus-highlight { animation: none; }
	}
</style>
